import { useMemo, useState } from "react";
import { FileText, Percent, Clock, CheckCircle2, Calculator } from "lucide-react";

const FINANCING_IMG =
  "https://images.unsplash.com/photo-1638262052640-82e94d64664a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxoYW5kc2hha2UlMjBjYXIlMjBrZXlzfGVufDB8fHx8MTc5MzgwNjQ3M3ww&ixlib=rb-4.1.0&q=85";

const BENEFITS = [
  {
    icon: Percent,
    title: "Soluções à Sua Medida",
    text: "Analisamos o seu perfil e apresentamos as opções de crédito mais adequadas ao seu orçamento.",
  },
  {
    icon: Clock,
    title: "Resposta Rápida",
    text: "Tratamos do processo junto das instituições financeiras para uma aprovação ágil.",
  },
  {
    icon: FileText,
    title: "Sem Complicações",
    text: "Ajudamos com a documentação e explicamos cada condição de forma clara e simples.",
  },
];

const fmtEUR = (n) =>
  n.toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";

export default function Financing() {
  const [amount, setAmount] = useState(25000);
  const [downPayment, setDownPayment] = useState(5000);
  const [months, setMonths] = useState(60);
  const [rate, setRate] = useState(7.5);

  const monthly = useMemo(() => {
    const principal = Math.max(amount - downPayment, 0);
    const r = rate / 100 / 12;
    if (principal <= 0) return 0;
    if (r <= 0) return principal / months;
    return (principal * r) / (1 - Math.pow(1 + r, -months));
  }, [amount, downPayment, months, rate]);

  return (
    <section id="financiamento" data-testid="financing-section" className="py-20 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="relative">
              <div className="absolute -bottom-4 -right-4 w-full h-full border border-[#0072C6]/20 hidden sm:block" />
              <img
                src={FINANCING_IMG}
                alt="Entrega de chaves de automóvel"
                data-testid="financing-image"
                loading="lazy"
                className="relative w-full h-[320px] sm:h-[420px] object-cover shadow-lg"
              />
            </div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2">
            <p className="text-xs font-semibold tracking-[0.35em] uppercase text-slate-500 mb-4">
              Apoio ao Financiamento
            </p>
            <h2
              data-testid="financing-heading"
              className="font-display text-3xl sm:text-4xl tracking-tight text-[#0072C6] leading-tight"
            >
              O Financiamento Certo para o Seu Automóvel
            </h2>
            <p className="mt-6 text-base leading-relaxed text-slate-600">
              Não deixe o financiamento ser um obstáculo. Acompanhamos todo o processo de crédito automóvel,
              com total clareza sobre condições, prazos e mensalidades.
            </p>

            <div className="mt-9 space-y-6" data-testid="financing-benefits">
              {BENEFITS.map(({ icon: Icon, title, text }, i) => (
                <div key={title} className="flex items-start gap-4" data-testid={`financing-benefit-${i}`}>
                  <span className="shrink-0 w-11 h-11 flex items-center justify-center border border-[#0072C6]/25 text-[#0072C6]">
                    <Icon className="w-5 h-5" />
                  </span>
                  <div>
                    <h3 className="font-semibold text-[#0072C6]">{title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-slate-600">{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Simulator */}
        <div
          id="simulador"
          data-testid="simulator-section"
          className="mt-20 bg-[#0072C6] text-white p-8 sm:p-12"
        >
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-11 h-11 flex items-center justify-center bg-white text-[#0072C6]">
                  <Calculator className="w-5 h-5" />
                </span>
                <h3 className="font-display text-2xl sm:text-3xl" data-testid="simulator-heading">
                  Simule o Seu Financiamento
                </h3>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="sim-amount" className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-2">
                    Valor da viatura (€)
                  </label>
                  <input
                    id="sim-amount"
                    data-testid="sim-amount-input"
                    type="number"
                    min={1000}
                    step={500}
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value) || 0)}
                    className="w-full bg-white/10 border border-white/25 px-4 py-3 text-sm text-white placeholder-slate-400 focus:outline-none focus:border-white"
                  />
                </div>
                <div>
                  <label htmlFor="sim-down" className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-2">
                    Entrada inicial (€)
                  </label>
                  <input
                    id="sim-down"
                    data-testid="sim-down-input"
                    type="number"
                    min={0}
                    step={500}
                    value={downPayment}
                    onChange={(e) => setDownPayment(Number(e.target.value) || 0)}
                    className="w-full bg-white/10 border border-white/25 px-4 py-3 text-sm text-white placeholder-slate-400 focus:outline-none focus:border-white"
                  />
                </div>
                <div>
                  <label htmlFor="sim-months" className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-2">
                    Prazo
                  </label>
                  <select
                    id="sim-months"
                    data-testid="sim-months-select"
                    value={months}
                    onChange={(e) => setMonths(Number(e.target.value))}
                    className="w-full bg-white/10 border border-white/25 px-4 py-3 text-sm text-white focus:outline-none focus:border-white [&>option]:text-slate-900"
                  >
                    {[24, 36, 48, 60, 72, 84, 96, 120].map((m) => (
                      <option key={m} value={m}>
                        {m} meses
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="sim-rate" className="block text-xs font-semibold uppercase tracking-wider text-slate-300 mb-2">
                    TAN (%)
                  </label>
                  <input
                    id="sim-rate"
                    data-testid="sim-rate-input"
                    type="number"
                    min={0}
                    max={30}
                    step={0.1}
                    value={rate}
                    onChange={(e) => setRate(Number(e.target.value) || 0)}
                    className="w-full bg-white/10 border border-white/25 px-4 py-3 text-sm text-white placeholder-slate-400 focus:outline-none focus:border-white"
                  />
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white text-[#0072C6] p-8 text-center" data-testid="simulator-result">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500">
                  Mensalidade estimada
                </p>
                <p className="mt-3 font-display text-4xl sm:text-5xl" data-testid="sim-monthly-value">
                  {fmtEUR(monthly)}
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  {months} meses · {fmtEUR(Math.max(amount - downPayment, 0))} financiados
                </p>
                <p className="mt-4 text-[11px] leading-relaxed text-slate-400">
                  Simulação meramente indicativa, sem valor contratual. A prestação final depende da aprovação
                  e das condições da instituição financeira.
                </p>
                <a
                  href="#contacto"
                  data-testid="simulator-cta"
                  onClick={() => {
                    sessionStorage.setItem("lead_inquiry", "financiamento");
                    window.dispatchEvent(new CustomEvent("lead:inquiry", { detail: "financiamento" }));
                  }}
                  className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-[#FF6A00] text-white font-semibold px-8 py-4 text-sm tracking-wide hover:bg-[#e05e00] transition-colors"
                >
                  Pedir Proposta de Financiamento
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
